// SDS design persistence: wrap sdk.saveDesign and normalize its callback into end-product data.

import { store } from '../../../core/store.js'
import { CONFIG } from '../../../core/config.js'
import { fetchWithTimeout, REQUEST_TIMEOUT_MS } from '../../../api/index.js'
import { createLogger } from '../../../utils/logger.js'

const log = createLogger('shopify')

/**
 * Save design to SDS
 * @returns {Promise<Object>} Design data
 */
export async function saveDesignToSDS() {
  return new Promise((resolve, reject) => {
    const sdk = store.getState('design.sdkInstance') || window.SDSManager?.state?.sdk

    log.info('saveDesignToSDS: Starting...')

    if (!sdk) {
      reject(new Error('SDK not available'))
      return
    }

    if (typeof sdk.saveDesign !== 'function') {
      log.error('saveDesign method not available. Available:', Object.keys(sdk))
      reject(new Error('saveDesign method not available'))
      return
    }

    try {
      log.info('Calling sdk.saveDesign()...')
      sdk.saveDesign((result) => {
        log.debug('saveDesign callback received')

        const resultStatus = result?.statue || result?.status

        if (result && resultStatus === 'success' && result.data) {
          const data = result.data

          // Extract image URLs (multiple sources)
          const imageUrls = data.imageUrls || data.imgUrlsList || []
          const thumbnailUrls = data.image_thumbnail_urls || []

          // Extract layer data
          const layerImages = (data.taskChildLayers || []).map((layer) => ({
            id: layer.id,
            type: layer.type,
            materialImgUrl: layer.materialImgUrl,
            materialImgDownUrl: layer.materialImgDownUrl,
            materialOriginImgUrl: layer.materialOriginImgUrl,
            width: layer.width,
            height: layer.height,
            print_width: layer.print_width,
            print_height: layer.print_height,
            fit_level: layer.fit_level,
            material_ids: layer.material_ids || [],
          }))

          const endProductData = {
            // Core ID
            designId: data.id,
            productId: data.id,
            keyId: data.keyId,
            taskId: data.task_id,
            taskChildId: data.task_child_id,

            // Base Product association
            productionId: data.productId,
            productParentId: data.productParentId,
            prototypeId: data.prototype_id,
            compoundId: data.compound_id,

            // Product info
            type: data.type,
            designProductType: data.design_product_type,
            productName: data.productName || data.exportName,
            merchantId: data.merchantId,

            // Variant info
            color: data.color,
            size: data.size,
            productColor: data.product_color,
            productSize: data.product_size,
            originalColor: data.originalColor,
            originalSize: data.originalSize,

            // Image
            images: imageUrls,
            thumbnails: thumbnailUrls,

            // Design data
            layerImages: layerImages,
            fitLevel: data.fit_level,
            materialImgName: data.material_img_name,

            // Raw data (for debugging and full storage)
            rawData: data,
          }

          log.info('Extracted end product data:', endProductData)
          persistDesignVersion(endProductData)
            .then((version) => resolve({ ...endProductData, ...version }))
            .catch(reject)
        } else {
          const errorMsg = result?.message || result?.msg || result?.error || 'Unknown error'
          log.error('saveDesignToSDS FAILED:', errorMsg)
          reject(new Error(errorMsg))
        }
      })
    } catch (error) {
      reject(error)
    }
  })
}

async function persistDesignVersion(endProductData) {
  const publishRequestKey = `mms_design_version_${String(endProductData.productId)}`
  const publishRequestId =
    sessionStorage.getItem(publishRequestKey) ||
    globalThis.crypto?.randomUUID?.() ||
    `design-${Date.now()}-${Math.random().toString(36).slice(2)}`
  sessionStorage.setItem(publishRequestKey, publishRequestId)

  const currentProduct = store.getState('design.currentProduct')
  const response = await fetchWithTimeout(
    `${CONFIG.SHOPIFY_API}/api/customer/designs/versions`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('mms_customer_token') || ''}`,
      },
      body: JSON.stringify({
        publishRequestId,
        visibility: 'private',
        sourceSnapshot: {
          sourceDesignId: String(endProductData.productId),
          supplierParentProductId: String(endProductData.productParentId || ''),
          supplierProductionId: endProductData.productionId
            ? String(endProductData.productionId)
            : null,
          keyId: endProductData.keyId ?? null,
          taskId: endProductData.taskId ?? null,
          taskChildId: endProductData.taskChildId ?? null,
          prototypeId: endProductData.prototypeId ?? null,
          compoundId: endProductData.compoundId ?? null,
          generationId: endProductData.generation_id || currentProduct?.generation_id || null,
          cardIds: endProductData.card_ids || currentProduct?.card_ids || [],
          images: endProductData.images || [],
          layerImages: endProductData.layerImages || [],
          rawData: endProductData.rawData,
        },
      }),
    },
    REQUEST_TIMEOUT_MS.WRITE
  )
  const result = await response.json().catch(() => null)
  if (!response.ok || !result?.success || !result.data?.design_version_id) {
    throw new Error(result?.error?.message || 'Failed to persist design version')
  }
  return {
    designId: result.data.design_id,
    designVersionId: result.data.design_version_id,
    designVersionNumber: result.data.version_number,
    qualityStatus: result.data.quality_status,
  }
}
